import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { TranslateModule } from '@ngx-translate/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

import { NotificationService } from '@shared/services';

import { ExpressionField, FieldDefinitionsService } from '../../common';
import {
  AGGREGATE_FUNCTIONS,
  DATE_PARTS,
  DateToken,
  FormulaOperator,
  FormulaResult,
  FormulaToken,
  OPERATORS,
  aggregateLabel,
  canAddClosingParenthesis,
  canAddOpeningParenthesis,
  formulaSnapshot,
  isOperatorTurn,
  newDateToken,
  operatorsAround,
  parseExpression,
  validateFormula,
  withoutToken,
} from './formula';

export type FormulaFieldSlot = 'field' | 'second' | 'third';

@Component({
  selector: 'cipo-field-formula',
  imports: [
    FlexLayoutModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    NgTemplateOutlet,
    NgxSkeletonLoaderModule,
    TranslateModule,
  ],
  templateUrl: './field-formula.component.html',
  styleUrl: './field-formula.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldFormulaComponent {
  private readonly fieldDefinitions = inject(FieldDefinitionsService);
  private readonly notify = inject(NotificationService);

  readonly moduleId = input.required<number>();
  readonly fieldId = input<number>(null);
  readonly expression = input<string>('');
  readonly isDateResult = input(false);
  readonly disabled = input(false);
  readonly fields = input<ExpressionField[]>();

  readonly loading = signal(true);
  readonly tokens = signal<FormulaToken[]>([]);
  readonly numericFields = signal<ExpressionField[]>([]);
  readonly dateFields = signal<ExpressionField[]>([]);
  readonly deleteMode = signal(false);
  readonly negativeNumber = signal(false);
  readonly invalidIndexes = signal(new Set<number>());
  readonly fieldQuery = signal('');

  private readonly initialSnapshot = signal<string>(null);
  private backup: FormulaToken[] = [];

  readonly functions = AGGREGATE_FUNCTIONS;
  readonly dateParts = DATE_PARTS;

  readonly changed = computed(
    () => this.initialSnapshot() !== null && formulaSnapshot(this.tokens()) !== this.initialSnapshot(),
  );
  readonly operatorTurn = computed(() => isOperatorTurn(this.tokens()));
  readonly canOpenParenthesis = computed(() => canAddOpeningParenthesis(this.tokens()));
  readonly canCloseParenthesis = computed(() => canAddClosingParenthesis(this.tokens()));
  readonly locked = computed(() => this.disabled() || this.loading());

  constructor() {
    effect(() => {
      const moduleId = this.moduleId();
      if (moduleId) {
        untracked(() => this.load());
      }
    });
  }

  load(): void {
    const fields = this.fields();
    if (fields) {
      this.applyFields(fields);
      return;
    }
    this.loading.set(true);
    this.fieldDefinitions.getExpressionFields(this.moduleId(), this.fieldId()).subscribe({
      next: fields => this.applyFields(fields),
      error: () => this.applyFields([]),
    });
  }

  result(): FormulaResult {
    const validation = validateFormula(this.tokens(), this.isDateResult());
    this.invalidIndexes.set(new Set(validation.invalidIndexes));
    validation.errors.forEach(error => this.notify.error(error));
    return validation.result ?? null;
  }

  // MARK: template helpers

  functionLabel(key: number): string {
    return aggregateLabel(key);
  }

  operatorLabel(value: FormulaOperator): string {
    return OPERATORS.find(entry => entry.value === value)?.label;
  }

  operatorsAt(index: number) {
    return operatorsAround(this.tokens(), index);
  }

  fieldsFor(slot: FormulaFieldSlot, token: FormulaToken): ExpressionField[] {
    let fields = this.dateFields();
    if (slot === 'field') {
      fields = this.numericFields();
    } else if (slot === 'second' && (token as DateToken).secondKind === 'number') {
      fields = this.numericFields().filter(field => field.isNumberFormatting);
    }
    const query = this.fieldQuery().trim().toLowerCase();
    return query ? fields.filter(field => (field.value ?? '').toLowerCase().includes(query)) : fields;
  }

  // MARK: toolbar

  addFunction(): void {
    this.append({ type: 'function', functionKey: AGGREGATE_FUNCTIONS[0].key });
  }

  addDateFunction(): void {
    const lastOperator = [...this.tokens()].reverse().find(token => token.type === 'operator');
    if (lastOperator && !OPERATORS.find(entry => entry.value === lastOperator.value)?.forDate) {
      this.notify.error('modules.fieldConfig.formula.dateOperatorNotAllowed');
      return;
    }
    this.append(newDateToken(this.isDateResult()));
  }

  addNumber(): void {
    this.append({ type: 'number', value: 0 });
  }

  addOperator(): void {
    this.append({ type: 'operator', value: OPERATORS[0].value });
  }

  addParenthesis(value: '(' | ')'): void {
    this.append({ type: 'parenthesis', value });
  }

  // MARK: token edits

  setFunction(index: number, functionKey: number): void {
    this.patch(index, { functionKey });
  }

  setField(index: number, slot: FormulaFieldSlot, field: ExpressionField): void {
    if (slot === 'field') {
      this.patch(index, { field });
    } else if (slot === 'second') {
      this.patch(index, { secondField: field });
    } else {
      this.patch(index, { thirdField: field });
    }
  }

  setDatePart(index: number, datePart: string): void {
    this.patch(index, { datePart });
  }

  toggleSecondParameter(index: number): void {
    const token = this.tokens()[index] as DateToken;
    this.patch(index, { useSecondField: !token.useSecondField, secondField: undefined, secondNumber: undefined });
  }

  setSecondNumber(index: number, raw: string): void {
    const value = raw === '' ? undefined : Number(raw);
    this.negativeNumber.set(value < 0);
    this.patch(index, { secondNumber: value });
  }

  setOperator(index: number, value: FormulaOperator): void {
    this.patch(index, { value });
  }

  setNumber(index: number, raw: string): void {
    const value = raw === '' ? 0 : Number(raw);
    this.negativeNumber.set(value < 0);
    this.patch(index, { value });
  }

  // MARK: delete mode

  enterDeleteMode(): void {
    this.backup = this.tokens();
    this.deleteMode.set(true);
  }

  removeToken(index: number): void {
    this.invalidIndexes.set(new Set());
    this.tokens.update(tokens => withoutToken(tokens, index));
  }

  finishDelete(): void {
    this.deleteMode.set(false);
  }

  cancelDelete(): void {
    this.tokens.set(this.backup);
    this.deleteMode.set(false);
  }

  // MARK: private

  private applyFields(fields: ExpressionField[]): void {
    const numeric = fields.filter(field => !field.isDate);
    const date = fields.filter(field => field.isDate);
    this.numericFields.set(numeric);
    this.dateFields.set(date);
    const tokens = parseExpression(this.expression(), numeric, date);
    this.tokens.set(tokens);
    this.initialSnapshot.set(formulaSnapshot(tokens));
    this.loading.set(false);
  }

  private append(token: FormulaToken): void {
    this.invalidIndexes.set(new Set());
    this.tokens.update(tokens => [...tokens, token]);
  }

  private patch(index: number, changes: Record<string, unknown>): void {
    this.invalidIndexes.update(indexes => {
      const next = new Set(indexes);
      next.delete(index);
      return next;
    });
    this.tokens.update(tokens =>
      tokens.map((token, position) => (position === index ? ({ ...token, ...changes } as FormulaToken) : token)),
    );
  }
}
